import {
  useActuators,
  useBodies,
  useCameras,
  useEqualities,
  useJoints,
  useLights,
  useSites,
  useTendons,
  useWorldBodies,
} from "../../stores/sceneStore";
import { findGeomNode } from "../../utils/geomSelection";
import { jointStructuralEqualIgnoreRefAndRange } from "../../utils/jointStructuralEqualIgnoreRef";
import { areResolvedGeomRefsEqual, findVisualGeomNodeForMesh } from "./inspectorTargetUtils";

export function useInspectorSelectionRecords({
  selectedActuator,
  selectedBody,
  selectedCamera,
  selectedEquality,
  selectedGeom,
  selectedInertial,
  selectedJoint,
  selectedLight,
  selectedMesh,
  selectedSite,
  selectedTendon,
}) {
  const selectedJointRecord = useJoints(
    (joints) => (selectedJoint ? (joints?.[selectedJoint] ?? null) : null),
    jointStructuralEqualIgnoreRefAndRange
  );
  const selectedActuatorRecord = useActuators(
    (actuators) => (selectedActuator ? actuators?.[selectedActuator] : undefined),
    Object.is
  );
  const selectedBodyName = useBodies(
    (allBodies) => (selectedBody ? allBodies?.[selectedBody]?.name || "" : ""),
    Object.is
  );
  const selectedBodyExists = useBodies(
    (allBodies) => Boolean(selectedBody && allBodies?.[selectedBody]),
    Object.is
  );
  const selectedInertialBodyName = useBodies((allBodies) => {
    const str = String(selectedInertial || "");
    const sepIdx = str.indexOf(":");
    const bodyUuid = sepIdx > 0 ? str.slice(0, sepIdx) : null;
    return bodyUuid ? allBodies?.[bodyUuid]?.name || "" : "";
  }, Object.is);
  const selectedInertialBodyExists = useBodies((allBodies) => {
    const str = String(selectedInertial || "");
    const sepIdx = str.indexOf(":");
    const bodyUuid = sepIdx > 0 ? str.slice(0, sepIdx) : null;
    return Boolean(bodyUuid && allBodies?.[bodyUuid]);
  }, Object.is);
  const resolvedSelectedGeom = useBodies(
    (allBodies) => (selectedGeom ? findGeomNode(allBodies, selectedGeom) : null),
    areResolvedGeomRefsEqual
  );
  const resolvedSelectedMeshVisualGeom = useBodies(
    (allBodies) => (selectedMesh ? findVisualGeomNodeForMesh(allBodies, selectedMesh) : null),
    areResolvedGeomRefsEqual
  );
  const resolvedActiveVisualGeom = resolvedSelectedGeom || resolvedSelectedMeshVisualGeom;
  const isSelectedBodyWorldRoot = useWorldBodies(
    (worldBodies) =>
      Boolean(
        selectedBody &&
        (worldBodies || []).some((entry) => {
          const uuid = typeof entry === "string" ? entry : entry?.uuid;
          return uuid === selectedBody;
        })
      ),
    Object.is
  );
  const selectedWorldBodyDisplayName = useWorldBodies((worldBodies) => {
    if (!selectedBody) return null;
    const found = (worldBodies || []).find((entry) => {
      const uuid = typeof entry === "string" ? entry : entry?.uuid;
      return uuid === selectedBody;
    });
    if (found && typeof found === "object") {
      return typeof found.name === "string" ? found.name : null;
    }
    return null;
  }, Object.is);

  const selectedSiteRecord = useSites((siteMap) => {
    if (!selectedSite) return undefined;
    return (
      siteMap?.[selectedSite] ||
      Object.values(siteMap || {}).find((entry) => entry?.name === selectedSite) ||
      undefined
    );
  }, Object.is);
  const selectedEqualityRecord = useEqualities(
    (equalities) => (selectedEquality ? equalities?.[selectedEquality] : undefined),
    Object.is
  );
  const allEqualities = useEqualities();
  const selectedTendonRecord = useTendons(
    (tendons) => (selectedTendon ? tendons?.[selectedTendon] : undefined),
    Object.is
  );
  const selectedCameraRecord = useCameras(
    (cameras) => (selectedCamera ? cameras?.[selectedCamera] : undefined),
    Object.is
  );
  const selectedLightRecord = useLights(
    (lights) => (selectedLight ? lights?.[selectedLight] : undefined),
    Object.is
  );
  const selectedGeomIsVisual = resolvedSelectedGeom?.geom?.visualOnly === true;
  const selectedGeomIsTendonWrap = useTendons((tendons) => {
    const geom = resolvedSelectedGeom?.geom;
    if (!selectedGeom || !geom) return false;
    const refs = new Set([String(geom.uuid || ""), String(geom.name || "")].filter(Boolean));
    return Object.values(tendons || {}).some(
      (tendon) =>
        tendon?.kind === "spatial" &&
        Array.isArray(tendon.spatial) &&
        tendon.spatial.some((entry) => entry?.kind === "geom" && refs.has(String(entry.geom || "")))
    );
  }, Object.is);
  const missingSelection =
    (selectedEquality &&
      !selectedEqualityRecord && {
        kind: "constraint",
        id: selectedEquality,
        message: "Selected constraint no longer exists",
      }) ||
    (selectedTendon &&
      !selectedTendonRecord && {
        kind: "tendon",
        id: selectedTendon,
        message: "Selected tendon no longer exists",
      }) ||
    (selectedJoint &&
      !selectedJointRecord && {
        kind: "joint",
        id: selectedJoint,
        message: "Selected joint no longer exists",
      }) ||
    (selectedCamera &&
      !selectedCameraRecord && {
        kind: "camera",
        id: selectedCamera,
        message: "Selected camera no longer exists",
      }) ||
    (selectedLight &&
      !selectedLightRecord && {
        kind: "light",
        id: selectedLight,
        message: "Selected light no longer exists",
      }) ||
    (selectedBody &&
      !selectedBodyExists && {
        kind: "body",
        id: selectedBody,
        message: "Selected body no longer exists",
      }) ||
    (selectedGeom &&
      !resolvedSelectedGeom && {
        kind: "geom",
        id: selectedGeom,
        message: "Selected geom no longer exists",
      }) ||
    (selectedInertial &&
      !selectedInertialBodyExists && {
        kind: "inertial",
        id: selectedInertial,
        message: "Selected inertial no longer exists",
      }) ||
    (selectedMesh &&
      !resolvedSelectedMeshVisualGeom && {
        kind: "visual mesh",
        id: selectedMesh,
        message: "Selected visual mesh no longer exists",
      }) ||
    (selectedSite &&
      !selectedSiteRecord && {
        kind: "site",
        id: selectedSite,
        message: "Selected site no longer exists",
      }) ||
    null;

  return {
    allEqualities,
    isSelectedBodyWorldRoot,
    missingSelection,
    resolvedActiveVisualGeom,
    resolvedSelectedGeom,
    resolvedSelectedMeshVisualGeom,
    selectedActuatorRecord,
    selectedBodyExists,
    selectedBodyName,
    selectedCameraRecord,
    selectedEqualityRecord,
    selectedGeomIsTendonWrap,
    selectedGeomIsVisual,
    selectedInertialBodyName,
    selectedJointRecord,
    selectedLightRecord,
    selectedSiteRecord,
    selectedTendonRecord,
    selectedWorldBodyDisplayName,
  };
}
